import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { X, Search as SearchIcon, ArrowLeft, SearchX } from 'lucide-react';
import { Content } from '@/types/content';
import { Story } from '@/utils/storiesData';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import SearchResultsList from './SearchResultsList';
interface HodinkeeSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (query: string) => void;
  contentResults: Content[];
  storyResults: Story[];
  isSearching: boolean;
  onTagClick?: (tag: string) => void;
}
const HodinkeeSearchModal: React.FC<HodinkeeSearchModalProps> = ({
  isOpen,
  onClose,
  onSearch,
  contentResults,
  storyResults,
  isSearching,
  onTagClick
}) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    onSearch(newQuery);
  };
  const clearSearch = () => {
    setQuery('');
    onSearch('');
    inputRef.current?.focus();
  };
  const handleItemClick = () => {
    onClose();
  };
  return <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[425px] md:max-w-[600px] lg:max-w-[800px] xl:max-w-[900px] p-0 gap-0 bg-netflix-black border-0">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="bg-netflix-black border-b border-gray-800">
          <div className="flex items-center p-4">
            <button className="mr-3 text-white/70 hover:text-white" onClick={onClose}>
              <ArrowLeft size={24} />
            </button>
            <div className="relative flex-1 flex items-center">
              <SearchIcon className="absolute left-2 h-5 w-5 text-gray-400" />
              <Input ref={inputRef} type="text" value={query} onChange={handleSearch} placeholder="Search for titles, people, genres..." className="pl-9 pr-9 py-2 bg-gray-800/60 border-0 text-white focus:ring-0 placeholder:text-gray-400" />
              {query && <button className="absolute right-2 text-gray-400 hover:text-white" onClick={clearSearch}>
                  <X size={18} />
                </button>}
            </div>
          </div>
        </div>
        
        <div className="overflow-y-auto max-h-[70vh] p-4 bg-netflix-black">
          {query.length === 0 ? <div className="text-center text-gray-400 py-0">
              <p>Start typing to search for titles, people, or genres</p>
            </div> : isSearching ? <div className="text-center text-gray-400 py-12">
              <p>Searching...</p>
            </div> : storyResults.length === 0 && contentResults.length === 0 ? <div className="text-center text-gray-400 py-12 flex flex-col items-center">
              <SearchX size={48} className="mb-4 text-gray-500" />
              <p>No results found for "{query}"</p>
              <p className="mt-2 text-sm">Try a different search term</p>
            </div> : <div className="animate-fade-in">
              <SearchResultsList storyResults={storyResults} contentResults={contentResults} onItemClick={handleItemClick} onTagClick={onTagClick} />
            </div>}
        </div>
      </DialogContent>
    </Dialog>;
};
export { HodinkeeSearchModal };